/**
 * AdminRoleSwitcher — sticky in-flow role switcher for every admin.
 * ==================================================================
 *
 * Aug 15, 2026 · Reconciliation with C3a + C3b:
 *
 *   1. Names the CURRENT context so an admin always knows which lens
 *      they are viewing through:
 *
 *        • Viewing as Admin
 *        • Viewing as Pupil
 *        • Viewing as PDI
 *        • Viewing as ADI
 *
 *      (Previously the pill only advertised the target roles as
 *       "Pupil · PDI · ADI ▼", which made it hard to tell which
 *       lens was active. Founder brief E.3 — Aug 15.)
 *
 *   2. Renders for EVERY admin state, including founder-mode. The
 *      old `if (user.operating_mode || user.founder_mode_active_role)
 *      return null` short-circuit was written against the assumption
 *      that FounderModeBanner owned the founder-mode return path;
 *      that banner was retired by C3b, so the switcher is now the
 *      single canonical exit surface for both preview-persona and
 *      founder-mode. The sheet chooses the correct backend endpoint
 *      based on the current state:
 *
 *        • preview persona (admin_view_role set) → PUT /admin/view-role
 *        • founder mode (operating_mode set)     → POST /admin/founder-mode/exit
 *
 *   3. Return-to-Admin ALWAYS navigates to /admin — pinned by
 *      `test_admin_role_switcher_return_navigates_to_admin` and
 *      `test_admin_role_switcher_back_button_lands_on_admin`.
 *
 * Everything else (mobile-safe sticky pill, WCAG-compliant 44px
 * tap target, no floating chrome, storage wipe on switch) is
 * preserved from the prior implementation.
 */
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Crown, ChevronDown, X, GraduationCap, ShieldCheck, Users, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { clearRoleScopedStorage } from "../utils/roleStorage";
import { MobileBottomSheet } from "./mobile";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { id: "learner", label: "Pupil", mode: "real", Icon: Users,          desc: "Learner / pupil view" },
  { id: "pdi",     label: "PDI",   mode: "real", Icon: GraduationCap,  desc: "Trainee instructor view" },
  { id: "adi",     label: "ADI",   mode: "real", Icon: ShieldCheck,    desc: "Qualified instructor view" },
];

// Canonical label for the current lens. Keep in sync with ROLES + Admin.
const CONTEXT_LABEL = {
  admin: "Admin",
  learner: "Pupil",
  pdi: "PDI",
  adi: "ADI",
};

// Resolve the current viewing context from the user's role/context
// fields. `admin_view_role` and `operating_mode` are set by two
// independent doctrines (preview persona / founder mode) and either
// changes which lens the admin is looking through. When neither is
// set, the admin is viewing as themselves.
export function currentContextKey(user) {
  if (!user) return "admin";
  const view = (user.admin_view_role || "").toString().trim().toLowerCase();
  if (view === "learner" || view === "pdi" || view === "adi") return view;
  // operating_mode is stored as "<kind>_<role>" (e.g. "real_learner",
  // "demo_pdi"). Extract the role suffix so founder-mode also
  // participates in the label.
  const op = (user.operating_mode || "").toString().trim().toLowerCase();
  if (op) {
    const parts = op.split("_");
    const role = parts[parts.length - 1];
    if (role === "learner" || role === "pdi" || role === "adi") return role;
  }
  const legacy = (user.founder_mode_active_role || "").toString().trim().toLowerCase();
  if (legacy === "learner" || legacy === "pdi" || legacy === "adi") return legacy;
  return "admin";
}

export function currentContextLabel(user) {
  return CONTEXT_LABEL[currentContextKey(user)] || "Admin";
}

// True when the admin is inside strict founder mode (as opposed to
// the lighter admin_view_role preview). Founder mode has its own
// exit endpoint that also clears operating_mode + founder_mode_kind.
function inFounderMode(user) {
  return !!(user && (user.operating_mode || user.founder_mode_active_role));
}

export default function AdminRoleSwitcher() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Body-scroll lock + ESC dismiss handled by <MobileBottomSheet> primitive.

  if (!user) return null;
  const isAdmin = user.role === "admin" || user.is_admin;
  if (!isAdmin) return null;
  // Aug 15, 2026 — no longer suppressed during founder mode. The
  // switcher is the single canonical exit + label surface for both
  // preview-persona and founder-mode states (FounderModeBanner
  // retired by C3b).

  const contextKey = currentContextKey(user);
  const contextLabel = CONTEXT_LABEL[contextKey] || "Admin";
  const founderMode = inFounderMode(user);

  const switchRole = async (mode, role) => {
    setBusy(true);
    try {
      clearRoleScopedStorage();
      const pupilProfileMatch = location.pathname.match(/^\/pupil\/([^/]+)/);
      const pupilId =
        role === "learner" && pupilProfileMatch
          ? decodeURIComponent(pupilProfileMatch[1])
          : null;
      const r = await axios.post(
        `${API}/api/admin/founder-mode/start`,
        { mode, role, ...(pupilId ? { pupil_id: pupilId } : {}) },
        { withCredentials: true }
      );
      await refreshUser();
      toast.success(`Now viewing as ${role.toUpperCase()}`);
      setOpen(false);
      navigate(r.data.redirect_to || "/", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Switch failed");
    }
    setBusy(false);
  };

  const goToAdmin = async () => {
    setBusy(true);
    try {
      if (founderMode) {
        // Strict founder-mode exit — clears operating_mode +
        // founder_mode_active_role + founder_mode_kind + admin_view_role.
        try {
          await axios.post(
            `${API}/api/admin/founder-mode/exit`,
            {},
            { withCredentials: true },
          );
          clearRoleScopedStorage();
          await refreshUser();
        } catch (_e) {
          // Non-fatal — the admin can still reach /admin manually.
        }
      } else {
        // Lighter preview-persona exit — just resets admin_view_role.
        try {
          await axios.put(
            `${API}/api/admin/view-role`,
            { view_role: "admin" },
            { withCredentials: true },
          );
          clearRoleScopedStorage();
          await refreshUser();
        } catch (_e) {
          // Non-fatal.
        }
      }
    } finally {
      setOpen(false);
      setBusy(false);
      navigate("/admin");
    }
  };

  return (
    <>
      {/* Sticky pill at top of every page (admin-only, both preview
          and founder-mode admin states). */}
      <div
        data-testid="admin-role-switcher"
        data-context={contextKey}
        data-founder-mode={founderMode ? "1" : "0"}
        className="sticky top-0 z-[55] w-full bg-amber-50 dark:bg-amber-900/20 border-b border-amber-300/60 dark:border-amber-800/40"
      >
        <div className="max-w-5xl mx-auto px-3 py-2 flex items-center gap-2">
          <Crown className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p
            data-testid="admin-role-switcher-current-label"
            className="text-xs sm:text-sm font-bold text-amber-900 dark:text-amber-200 flex-1 truncate"
          >
            Viewing as {contextLabel}
            {founderMode ? " · founder mode" : ""}
          </p>
          <button
            data-testid="admin-role-switcher-toggle"
            onClick={() => setOpen(true)}
            className="text-xs font-bold uppercase tracking-wide bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 flex-shrink-0 min-h-[36px]"
          >
            Switch view <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Bottom sheet (mobile-first, fully wraps; never overflows) */}
      {open && (
        <MobileBottomSheet
          bareContent
          open={open}
          onClose={() => setOpen(false)}
          testid="admin-role-switcher-sheet-wrap"
        >
          <div
            data-testid="admin-role-switcher-sheet"
            onClick={(e) => e.stopPropagation()}
            className="w-full bg-white dark:bg-slate-900 rounded-t-3xl border-t border-slate-200 dark:border-slate-700 shadow-2xl max-h-screen-safe overflow-y-auto pb-safe"
          >
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3 sticky top-0 bg-white dark:bg-slate-900 z-10">
              <Crown className="w-5 h-5 text-amber-500" />
              <h2 className="font-bold text-base flex-1 text-slate-900 dark:text-slate-100">
                Switch view
              </h2>
              <button
                data-testid="admin-role-switcher-close"
                onClick={() => setOpen(false)}
                className="p-2 -mr-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-4 space-y-2">
              {/* Return-to-Admin is the most important escape. Pin it
                  at the top so an admin previewing/founder-moding
                  can always bail out quickly. */}
              <button
                data-testid="admin-role-switcher-back"
                onClick={goToAdmin}
                disabled={busy}
                className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors disabled:opacity-60 text-left"
              >
                <div className="w-10 h-10 rounded-xl bg-amber-200 dark:bg-amber-800/50 flex items-center justify-center flex-shrink-0">
                  <ArrowLeft className="w-5 h-5 text-amber-700 dark:text-amber-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-base text-slate-900 dark:text-slate-100">
                    Return to Admin
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {founderMode
                      ? "Exits founder mode · clears preview state"
                      : "Admin control panel · clears preview state"}
                  </p>
                </div>
              </button>

              <div className="h-px bg-slate-200 dark:bg-slate-800 my-1" />

              {ROLES.map((r) => (
                <button
                  key={r.id}
                  data-testid={`admin-role-switcher-${r.id}`}
                  onClick={() => switchRole(r.mode, r.id)}
                  disabled={busy}
                  className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-amber-400 dark:hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors disabled:opacity-60 text-left"
                >
                  <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                    <r.Icon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-base text-slate-900 dark:text-slate-100">
                      {r.label}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {r.desc}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            <div className="px-5 py-3 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400">
              Stripe is bypassed for admin — no real charges.
            </div>
          </div>
        </MobileBottomSheet>
      )}
    </>
  );
}
